import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const navigate = useNavigate();

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const response = await axios.get(`${API}/admin/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort customers
  const filteredCustomers = useMemo(() => {
    let result = [...customers];
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(c => 
        (c.name && c.name.toLowerCase().includes(search)) ||
        c.email.toLowerCase().includes(search) ||
        (c.phone && c.phone.toLowerCase().includes(search))
      );
    }
    
    // Role filter
    if (roleFilter !== 'all') {
      result = result.filter(c => c.role === roleFilter);
    }
    
    // Sorting
    result.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'email':
          aVal = a.email.toLowerCase();
          bVal = b.email.toLowerCase();
          break;
        case 'created_at':
        default:
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
      }
      
      if (sortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });
    
    return result;
  }, [customers, searchTerm, roleFilter, sortBy, sortOrder]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0">
        <div>
          <h2 className="text-xl sm:text-2xl font-heading font-bold">Kunden ({filteredCustomers.length})</h2>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1">Verwalten Sie Ihre Investoren</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary text-sm sm:text-base"
          data-testid="create-customer-button"
        >
          + Neuer Kunde
        </button>
      </div>

      {/* Search and Filter Bar */}
      <div className="card mb-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Suchen nach Name, E-Mail oder Telefon..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
                data-testid="customer-search-input"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="input-field w-auto"
              data-testid="customer-role-filter"
            >
              <option value="all">Alle Rollen</option>
              <option value="CUSTOMER">Kunden</option>
              <option value="ADMIN">Admins</option>
            </select>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [col, ord] = e.target.value.split('-');
                setSortBy(col);
                setSortOrder(ord);
              }}
              className="input-field w-auto"
              data-testid="customer-sort-select"
            >
              <option value="created_at-desc">Neueste zuerst</option>
              <option value="created_at-asc">Älteste zuerst</option>
              <option value="name-asc">Name A-Z</option>
              <option value="name-desc">Name Z-A</option>
              <option value="email-asc">E-Mail A-Z</option>
              <option value="email-desc">E-Mail Z-A</option>
            </select>
          </div>
        </div>
      </div>

      <div className="hidden md:block">
        <div className="card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b text-left">
                  <th 
                    className="py-3 px-4 font-semibold text-sm cursor-pointer hover:text-primary transition"
                    onClick={() => handleSort('name')}
                  >
                    Name <SortIcon column="name" />
                  </th>
                  <th 
                    className="py-3 px-4 font-semibold text-sm cursor-pointer hover:text-primary transition"
                    onClick={() => handleSort('email')}
                  >
                    E-Mail <SortIcon column="email" />
                  </th>
                  <th className="py-3 px-4 font-semibold text-sm">Rolle</th>
                  <th className="py-3 px-4 font-semibold text-sm">2FA</th>
                  <th 
                    className="py-3 px-4 font-semibold text-sm cursor-pointer hover:text-primary transition"
                    onClick={() => handleSort('created_at')}
                  >
                    Erstellt <SortIcon column="created_at" />
                  </th>
                  <th className="py-3 px-4 font-semibold text-sm">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-muted-foreground">
                      {searchTerm || roleFilter !== 'all' ? 'Keine Kunden gefunden' : 'Noch keine Kunden vorhanden'}
                    </td>
                  </tr>
                ) : (
                  filteredCustomers.map((customer) => (
                    <tr key={customer.id} className="border-b hover:bg-background-subtle transition" data-testid={`customer-row-${customer.id}`}>
                      <td className="py-3 px-4">
                        <span className="font-medium">{customer.name || '-'}</span>
                      </td>
                      <td className="py-3 px-4 text-sm">{customer.email}</td>
                      <td className="py-3 px-4">
                        <span className="text-xs px-2 py-1 rounded bg-secondary">{customer.role}</span>
                      </td>
                      <td className="py-3 px-4">
                        {customer.twofa_enabled ? (
                          <span className="accent-badge">Aktiv</span>
                        ) : customer.twofa_required ? (
                          <span className="text-xs text-warning">Erforderlich</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Aus</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {format(new Date(customer.created_at), 'dd.MM.yyyy', { locale: de })}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => navigate(`/admin/customers/${customer.id}`)}
                          className="text-sm text-primary hover:underline font-medium"
                          data-testid={`view-customer-${customer.id}`}
                        >
                          Details →
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="md:hidden space-y-3">
        {filteredCustomers.length === 0 ? (
          <div className="card text-center py-8 text-muted-foreground">
            {searchTerm || roleFilter !== 'all' ? 'Keine Kunden gefunden' : 'Noch keine Kunden vorhanden'}
          </div>
        ) : (
          filteredCustomers.map((customer) => (
            <div 
              key={customer.id} 
              className="card cursor-pointer hover:shadow-md transition"
              onClick={() => navigate(`/admin/customers/${customer.id}`)}
              data-testid={`customer-card-${customer.id}`}
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="font-semibold text-base">{customer.name || customer.email}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{customer.email}</p>
                </div>
                {customer.twofa_enabled && (
                  <span className="accent-badge text-xs">2FA</span>
                )}
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t">
                <span className="text-xs px-2 py-1 rounded bg-secondary">{customer.role}</span>
                <span className="text-xs text-muted-foreground">
                  {format(new Date(customer.created_at), 'dd.MM.yy', { locale: de })}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {showCreateModal && (
        <CreateCustomerModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadCustomers();
          }}
        />
      )}
    </div>
  );
};

const CreateCustomerModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    twofa_required: false,
    bank_account_holder: '',
    bank_iban: '',
    bank_bic: '',
    bank_name: '',
  });
  const [loading, setLoading] = useState(false);
  const [showBankSection, setShowBankSection] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/customers`, formData);
      toast.success('Kunde erstellt und Einladung versendet');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="create-customer-modal">
        <h3 className="text-xl font-heading font-bold mb-4">Neuen Kunden anlegen</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium mb-1">E-Mail *</label>
              <input
                type="email"
                className="input-field"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="customer-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                className="input-field"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="customer-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Telefon</label>
              <input
                type="tel"
                className="input-field"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="customer-phone-input"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="twofa_required"
              checked={formData.twofa_required}
              onChange={(e) => setFormData({ ...formData, twofa_required: e.target.checked })}
              data-testid="customer-2fa-checkbox"
            />
            <label htmlFor="twofa_required" className="text-sm">2FA verpflichtend</label>
          </div>

          {/* Bank Details Section */}
          <div className="border-t pt-4 mt-4">
            <button
              type="button"
              onClick={() => setShowBankSection(!showBankSection)}
              className="flex items-center space-x-2 text-sm font-medium text-primary hover:text-accent transition"
            >
              <svg className={`w-4 h-4 transition-transform ${showBankSection ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span>Bankverbindung hinzufügen (optional)</span>
            </button>

            {showBankSection && (
              <div className="mt-4 space-y-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <label className="block text-sm font-medium mb-1">Kontoinhaber</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.bank_account_holder}
                    onChange={(e) => setFormData({ ...formData, bank_account_holder: e.target.value })}
                    placeholder="Max Mustermann"
                    data-testid="customer-bank-holder-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">IBAN</label>
                  <input
                    type="text"
                    className="input-field font-mono"
                    value={formData.bank_iban}
                    onChange={(e) => setFormData({ ...formData, bank_iban: e.target.value.toUpperCase().replace(/\s/g, '') })}
                    placeholder="DE89 3704 0044 0532 0130 00"
                    maxLength={34}
                    data-testid="customer-bank-iban-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">BIC</label>
                    <input
                      type="text"
                      className="input-field font-mono"
                      value={formData.bank_bic}
                      onChange={(e) => setFormData({ ...formData, bank_bic: e.target.value.toUpperCase() })}
                      placeholder="COBADEFFXXX"
                      maxLength={11}
                      data-testid="customer-bank-bic-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Bank</label>
                    <input
                      type="text"
                      className="input-field"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      placeholder="Commerzbank AG"
                      data-testid="customer-bank-name-input"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex space-x-3 mt-6">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Abbrechen</button>
            <button type="submit" className="btn-primary flex-1" disabled={loading} data-testid="customer-submit-button">
              {loading ? 'Erstellen...' : 'Erstellen & Einladen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminCustomerList;
